import { PropsWithChildren } from "react";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl, IButtonProps } from "@/Common/Components/Buttons/ButtonControl";
import { Locale } from "@/Common/Models/Localization/Locale";

/**
 * The properties for a {@link Popup} element.
 */
interface IPopupProps {
    /**
     * The text to show in the popup's title area above the message.
     *
     * Ignored if {@link titleAlternateComponent} is set.
     */
    title?: string;

    /**
     * Custom CSS classes (space-delimited) to add to the main element. Default none.
     */
    cssClass?: string;

    /**
     * A registered operation code to dispatch through {@link sessionStore.dispatch} when the popup is closed. Default none.
     */
    closeTarget?: number;

    /**
     * A callback to invoke when the popup is closed. Default none.
     */
    onCloseDelegate?: () => void;

    /**
     * Whether the user can drag the popup to reposition it. Default false.
     *
     * Ignored if {@link altView} or {@link titleAlternateComponent} is true.
     */
    draggable?: boolean;

    /**
     * The absolute top Y pixel at which to position the popup. Defaults to an auto-calculated position that takes other open popups into account.
     *
     * Ignored if {@link disableAutoStyling} are true.
     */
    topPosition?: number;

    /**
     * The absolute left X pixel at which to position the popup. Defaults to an auto-calculated position.
     *
     * Ignored if {@link centeredHorizontally} or {@link disableAutoStyling} are true.
     */
    leftPosition?: number;

    /**
     * The unique HTML ID for this specific popup. Defaults to an auto-generate ID unique within this session.
     */
    Id?: string;

    /**
     * Whether to render a minimal version of the popup which defers the layout to the children. Default false.
     */
    altView?: boolean;

    /**
     * TODO: unused.
     */
    dragClass?: string;

    /**
     * The default pixel height of the popup element. Default auto-calculated.
     *
     * Ignored if {@link disableAutoStyling} is true.
     */
    height?: number;

    /**
     * Whether content which extends past the popup bounds should be visible. Default false.
     *
     * Ignored if {@link disableAutoStyling} is true.
     */
    isOverflowVisible?: boolean;

    /**
     * Custom CSS styles to apply to the main element. Default none.
     *
     * These are partly overridden by props like {@link topPosition} and {@link centeredHorizontally} if they're set, unless you set {@link disableAutoStyling}.
     */
    style?: React.CSSProperties;

    /**
     * Whether the popup should automatically add an overlay that disables interacting with elements behind the popup. The transparancy is set via {@link isOverlayTransparent}. Default false.
     */
    manageOverlay?: boolean;

    /**
     * The HTML ID of the element to which to add the popup. Defaults to the HTML body.
     */
    containmentIdParent?: string;

    /**
     * Whether the overlay added via {@link manageOverlay} is fully transparent. Default false, which makes it semi-transparent.
     */
    isOverlayTransparent?: boolean;

    /**
     * A custom element to append to the bottom of the popup (after the standard footer if present). Default none.
     */
    customFooter?: JSX.Element;

    /**
     * Whether to hide the 'X' close button in the popup title area. Default false.
     */
    hideX?: boolean;

    /**
     * Whether to disable the auto-generated styles, so only CSS classes and {@link style} are applied.
     */
    disableAutoStyling?: boolean;

    /**
     * Whether to auto-position the popup in the horizontal middle of the screen. Default false.
     *
     * Ignored if {@link disableAutoStyling} is true.
     */
    centeredHorizontally?: boolean;

    /**
     * The default pixel width of the popup element. Default auto-calculated.
     *
     * Ignored if {@link disableAutoStyling} is true.
     */
    width?: number;

    /**
     * A custom element to render instead of the default title area. Default none.
     *
     * This disables title props like {@link title} and {@link draggable}.
     */
    titleAlternateComponent?: React.ReactNode;

    /**
     * Whether the {@link title} should be styled as uppercase text.
     *
     * Ignored if {@link titleAlternateComponent} is set.
     */
    titleLowerCase?: boolean;

    /**
     * If present, adds a standard popup footer with a cancel button and OK button. Default none.
     */
    standardFooterProps?: {
        /**
         * The props to pass to the cancel button. Default none.
         */
        cancelButton?: Partial<IButtonProps>;

        /**
         * The props to pass to the OK button. Default none.
         */
        okButton?: Partial<IButtonProps>;
    }

    /**
     * The locales for which {@link cacheStore.GetString} can get translations. This will asynchronously load translations for the first locale if it's not loaded yet.
     */
    acceptableLanguages?: Locale[];

    /**
     * An identifier used to target this modal by Cypress tests, added as a `data-cy` attribute on the main element.
     */
    dataCy?: string;

    /**
     * Custom CSS classes (space-delimited) to add to the header element. Default none.
     */
    headerClass?: string;
}

class PopupState {
    public topPosition: number;
}

class Popup extends BaseReactComponent<PropsWithChildren<IPopupProps>, PopupState> {
    private overlayId: string;
    private componentId: string;
    private globalPopupIndex: number;
    private height: number;

    /**
     * The initial popup styles. This shouldn't change after the component is mounted, to avoid breaking drag & drop behavior.
     */
    private initialStyles: React.CSSProperties;

    private get previousPopup(): Popup {
        return this.globalPopupIndex == 0 ? null : sessionStore.popups[this.globalPopupIndex - 1];
    }

    private get nextPopup(): Popup {
        return this.globalPopupIndex == (sessionStore.popups.length - 1) ? null : sessionStore.popups[this.globalPopupIndex + 1];
    }

    public get top(): number {
        return this.previousPopup
            ? this.previousPopup.bottom + 5 /* popupToPopupMargin */
            : window.innerHeight * 0.15; /* 15% of window height */
    }

    public get bottom(): number {
        return this.top + this.height;
    }

    constructor(props: PropsWithChildren<IPopupProps>) {
        super(props);
        this.state = new PopupState();
        this.registerSelfInSessionStore();

        if (this.props.acceptableLanguages?.any())
            this.setAcceptableLocales(this.props.acceptableLanguages);

        const id = sessionStore.newId();
        this.overlayId = `popup_${id}`;
        this.componentId = this.props.Id || id.toString();
        this.initialStyles = {
            marginTop: "10px",
            ...this.props.style,
            left: !this.props.centeredHorizontally
                ? this.props.leftPosition ? `${this.props.leftPosition}px` : this.props.style?.left
                : null,
            width: this.props.width
                ? `${this.props.width}px`
                : this.props.style?.width,
            height: this.props.height
                ? `${this.props.height}px`
                : this.props.style?.height,
            overflow: this.props.isOverflowVisible
                ? "visible"
                : this.props.style?.overflow
        };
    }

    public componentDidMount(): void {
        this.mutateState(s => this.updateTopPosition(s, this.props));

        if (this.props.manageOverlay)
            super.setOverlay(this.overlayId, { transparent: this.props.isOverlayTransparent });

        if (this.props.draggable == null || this.props.draggable) {
            let elem = $("#" + this.componentId);
            let cnt = this.props.containmentIdParent ? document.getElementById(this.props.containmentIdParent) : document.body;

            let dragOptions = {
                containment: cnt,
                handle: ".dragHandle",
                stop: () => {
                    elem.draggable("destroy");
                    elem.css("height", "auto");
                    window.setTimeout(() => elem.draggable(dragOptions), 50);
                },
                start: () => {
                    if (this.props.draggable) {
                        elem.removeClass("centered-horizontally");
                    }
                }
            };
            elem.draggable(dragOptions);
            $(window).trigger("resize");
        }
    }

    public componentDidUpdate(prevProps: PropsWithChildren<IPopupProps>): void {
        if (prevProps.topPosition != this.props.topPosition)
            this.mutateState(s => this.updateTopPosition(s, this.props));

        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        if (this.props.draggable == null || this.props.draggable)
            $("#" + this.componentId).draggable("destroy");

        this.unregisterSelfFromSessionStore();
        super.componentWillUnmount();
    }

    public render(): JSX.Element {
        return this.props.altView
            ? this.renderNewView()
            : this.renderOldView();
    }

    private renderOldView(): JSX.Element {
        // get popup classes
        let classNames = "popup";
        if (this.props.centeredHorizontally)
            classNames += " centered-horizontally";
        if (this.props.cssClass)
            classNames += " " + this.props.cssClass;

        const headerClassName = `row p-header ${this.props.headerClass ?? ""}`;

        // render
        return (
            <div
                id={this.componentId}
                className={classNames}
                style={this.getPositionCss()}
                data-cy={this.props.dataCy}
                ref={element => this.height = element?.getBoundingClientRect().height || this.height}
            >
                <div className={headerClassName}>
                    <div className="col-lg-12  col-md-12 col-sm-12">
                        {!this.props.hideX && <a className="close" aria-label="Close" onClick={() => this.onCloseClick()}> ×</a>}
                        {this.props.titleAlternateComponent || (
                            <h4
                                className={(!this.props.titleLowerCase ? "uppercase" : "") + (this.props.draggable == null || this.props.draggable ? " dragHandle" : "")}
                            >
                                {this.props.title}
                            </h4>
                        )}
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-12 col-md-12 col-sm-12">
                        {this.props.children}
                    </div>
                </div>
                {this.props.standardFooterProps && (
                    <div className="p-footer">
                        <div className="row">
                            <div className="col-sm-12 columns text-right">
                                <div>
                                    <ButtonControl
                                        className="btn btn-default push-right-5"
                                        onClick={() => this.props.onCloseDelegate?.()}
                                        label={this.getString("CancelLabel")}
                                        {...this.props.standardFooterProps.cancelButton}
                                    />
                                    <ButtonControl
                                        variant="brand"
                                        label={this.getString("OKLabel")}
                                        {...this.props.standardFooterProps.okButton}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {this.props.customFooter}
            </div>
        );
    }

    private renderNewView(): JSX.Element {
        return (
            <div
                id={this.componentId}
                className={`popup ${this.props.centeredHorizontally ? "centered-horizontally" : ""} ${this.props.cssClass || ""}`}
                data-cy={this.props.dataCy}
                style={this.getPositionCss()}
            >
                {this.props.children}
            </div>
        );
    }

    public decrementNextAndCurrentIndex(): void {
        this.nextPopup?.decrementNextAndCurrentIndex();
        this.globalPopupIndex--;
    }

    private updateTopPosition(state: PopupState, props: PropsWithChildren<IPopupProps>): void {
        state.topPosition = props.topPosition || this.top;
    }

    private getPositionCss(): React.CSSProperties {
        return !this.props.disableAutoStyling
            ? {
                ...this.initialStyles,
                top: `${this.state.topPosition}px`
            }
            : { ...this.props.style };
    }

    private registerSelfInSessionStore(): void {
        this.globalPopupIndex = sessionStore.popups.length;
        sessionStore.popups.push(this);
    }

    private unregisterSelfFromSessionStore(): void {
        this.nextPopup?.decrementNextAndCurrentIndex();

        sessionStore.popups.splice(this.globalPopupIndex, 1);
    }

    private onCloseClick(): void {
        this.props.closeTarget && sessionStore.dispatch(this.props.closeTarget);
        this.props.onCloseDelegate?.();
    }
}

export { Popup };
